import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";

export const Logo = ({ className = "", light = false }) => (
  <Link to="/" className={`logo ${className}`} data-testid="logo">
    <span className={light ? "text-white" : "logo-neo"}>Neo</span>
    <span className="logo-body">Body</span>
  </Link>
);

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { 
      name: "Solutions", 
      href: "/services",
      dropdown: [
        { name: "Client Management", href: "/services/client-management" },
        { name: "Scheduling", href: "/services/scheduling" },
        { name: "Documentation", href: "/services/documentation" },
        { name: "Analytics", href: "/services/analytics" },
        { name: "Integrations", href: "/services/integrations" },
        { name: "Security", href: "/services/security" },
      ]
    },
    { name: "About", href: "/about" },
    { name: "Blog", href: "/blog" },
    { name: "Contact", href: "/contact" },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 h-20 flex items-center transition-all duration-500 ${
          isScrolled ? "nav-glass" : "bg-transparent"
        }`}
        data-testid="navigation"
      >
        <div className="w-full max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between">
          <Logo />

          <div className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <div key={link.name} className="relative group">
                {link.dropdown ? (
                  <button 
                    className="nav-link flex items-center gap-1"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    {link.name}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                ) : (
                  <Link to={link.href} className="nav-link" data-testid={`nav-${link.name.toLowerCase()}`}>
                    {link.name}
                  </Link>
                )}
                
                {link.dropdown && (
                  <div 
                    className="absolute top-full left-0 pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <div className="bg-white rounded-2xl shadow-xl border border-[#1B4D3E]/10 py-4 min-w-[240px]">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          className="block px-6 py-3 text-[#4A4A4A] hover:text-[#1B4D3E] hover:bg-[#F9F9F7] transition-colors"
                        >
                          {item.name}
                        </Link>
                      ))}
                      <div className="border-t border-[#1B4D3E]/10 mt-2 pt-2">
                        <Link
                          to="/services"
                          className="block px-6 py-3 text-[#1B4D3E] font-medium hover:bg-[#F9F9F7] transition-colors"
                        >
                          View All Services
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="hidden lg:block">
            <Link to="/book" className="btn-primary" data-testid="nav-cta">
              Request Demo
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6 text-[#1B4D3E]" />
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed inset-0 bg-[#F9F9F7] z-[100] overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="p-6 min-h-full flex flex-col">
              <div className="flex items-center justify-between mb-12">
                <Logo />
                <button onClick={() => setIsMobileMenuOpen(false)} data-testid="close-mobile-menu">
                  <X className="w-6 h-6 text-[#1B4D3E]" />
                </button>
              </div>
              
              <div className="flex-1 flex flex-col gap-2">
                {navLinks.map((link, i) => (
                  <div key={link.name}>
                    {link.dropdown ? (
                      <div>
                        <Link
                          to={link.href}
                          className="block text-2xl font-display text-[#1A1A1A] py-4 border-b border-[#1B4D3E]/10"
                        >
                          {link.name}
                        </Link>
                        <div className="pl-4">
                          {link.dropdown.map((item) => (
                            <Link
                              key={item.name}
                              to={item.href}
                              className="block text-lg text-[#4A4A4A] py-3"
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        to={link.href}
                        className="block text-2xl font-display text-[#1A1A1A] py-4 border-b border-[#1B4D3E]/10"
                      >
                        {link.name}
                      </Link>
                    )}
                  </div>
                ))}
              </div>
              
              <Link to="/book" className="btn-gold w-full justify-center mt-8">
                Request Demo
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export const Footer = () => {
  const footerLinks = {
    services: [
      { name: "Client Management", href: "/services/client-management" },
      { name: "Scheduling", href: "/services/scheduling" },
      { name: "Documentation", href: "/services/documentation" },
      { name: "Analytics", href: "/services/analytics" },
      { name: "Integrations", href: "/services/integrations" },
    ],
    company: [
      { name: "About Us", href: "/about" },
      { name: "Blog", href: "/blog" },
      { name: "Contact", href: "/contact" },
    ],
    support: [
      { name: "Request Demo", href: "/book" },
      { name: "FAQ", href: "/contact#faq" },
      { name: "Privacy Policy", href: "/privacy" },
      { name: "Terms of Service", href: "/terms" },
    ]
  };

  return (
    <footer className="footer py-20 md:py-24" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
          <div className="lg:col-span-2">
            <Logo className="text-3xl mb-6" />
            <p className="text-[#4A4A4A] font-light leading-relaxed max-w-md mb-8">
              Powerful client management software built for healthcare companies. 
              Streamline operations and focus on patient care.
            </p>
            <div className="flex gap-4">
              {["Instagram", "LinkedIn", "Twitter", "Facebook"].map((social) => (
                <a
                  key={social}
                  href="#"
                  className="w-10 h-10 rounded-full border border-[#1B4D3E]/20 flex items-center justify-center text-[#1B4D3E] hover:bg-[#1B4D3E] hover:text-white hover:border-[#1B4D3E] transition-all"
                  data-testid={`social-${social.toLowerCase()}`}
                >
                  <span className="sr-only">{social}</span>
                </a>
              ))}
            </div>
          </div>
          
          <div>
            <h4 className="font-display text-lg text-[#1A1A1A] mb-6">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((item) => (
                <li key={item.name}>
                  <Link to={item.href} className="footer-link">{item.name}</Link>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="font-display text-lg text-[#1A1A1A] mb-6">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((item) => (
                <li key={item.name}>
                  <Link to={item.href} className="footer-link">{item.name}</Link>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="font-display text-lg text-[#1A1A1A] mb-6">Support</h4>
            <ul className="space-y-3">
              {footerLinks.support.map((item) => (
                <li key={item.name}>
                  <Link to={item.href} className="footer-link">{item.name}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="border-t border-[#1B4D3E]/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-[#888]">
            © {new Date().getFullYear()} NeoBody. All rights reserved.
          </p>
          <p className="text-sm text-[#888]">
            HIPAA compliant. SOC 2 Type II certified.
          </p>
        </div>
      </div>
    </footer>
  );
};

export const PageHeader = ({ title, subtitle, breadcrumbs = [] }) => (
  <div className="bg-[#1B4D3E] pt-32 pb-20 md:pt-40 md:pb-28">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      {breadcrumbs.length > 0 && (
        <div className="flex items-center gap-2 text-white/60 text-sm mb-6">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          {breadcrumbs.map((crumb, i) => (
            <span key={i} className="flex items-center gap-2">
              <span>/</span>
              {crumb.href ? (
                <Link to={crumb.href} className="hover:text-white transition-colors">{crumb.name}</Link>
              ) : (
                <span className="text-white">{crumb.name}</span>
              )}
            </span>
          ))}
        </div>
      )}
      <h1 className="font-display text-4xl md:text-6xl text-white mb-4">{title}</h1>
      {subtitle && <p className="text-lg md:text-xl text-white/70 font-light max-w-2xl">{subtitle}</p>}
    </div>
  </div>
);
